import * as React from 'react';
import styled from 'styled-components';
import { BREAKPOINTS, COLORS, scrollBarStyling } from '../../styles';

import MenuLink from './MenuLink';
import { UserMenuItem } from '../../types';
import { Button, UIIconButton } from '../../Button';
import { ArrowLineLeft, Close } from '../../icons';
import { flowDown, Right, UserMenuSectionListStyling } from '../mobile/CommonStyles';

const Menu = styled.ul`
  list-style: none;
  margin: 0;
  background-color: ${COLORS.white};
  box-sizing: border-box;
  display: none;
  -webkit-animation: ${flowDown} 0.2s ease-in-out;
  animation: ${flowDown} 0.2s ease-in-out;
  position: absolute;
  border-radius: 4px;
  z-index: 100;
  height: 100%;
  width: 320px;
  padding-inline-start: 0;
  overflow-y: scroll;
  ${BREAKPOINTS.SMALL} {
    min-width: 360px;
  }

  .open & {
    display: flex;
    flex-direction: column;
  }

  .signOutButton {
    height: 40px !important;
    width: 288px !important;

    ${BREAKPOINTS.SMALL} {
      width: 328px !important;
    }
  }

  ${scrollBarStyling};
`;

const MenuSection = styled.li`
  padding: 8px 0;
  position: relative;

  .disabled-link {
    color: #21212150;
    pointer-events: none;
    key-events: none;
  }
`;

const MenuSectionList = styled.ul`
  list-style: none;
  margin: 0;
  overflow-y: unset !important;
  padding-inline-start: 0;
  ${UserMenuSectionListStyling};
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 22px 12px 0 12px;
  color: ${COLORS.neutral_600};

  h3 {
    margin-block-start: 0 !important;
    margin-block-end: 0 !important;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    font-weight: normal;
    font-size: 16px;
    color: inherit;
    margin-left: 24px;
  }
`;

const LabelLine = styled.span`
  height: 32px;
  background-color: ${COLORS.neutral_20};
  padding-left: 24px;
  display: flex;
  line-height: 32px;
`;

const StyledMenulink = styled(MenuLink)`
  width: 100%;
  height: 48px;
  margin: 0;
  line-height: 48px;
`;

interface Props {
  accountMenuHeader: string;
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  clickMenuAction: () => void;
  accountSection?: UserMenuItem[];
  organizationSection?: UserMenuItem[];
  supportSection?: UserMenuItem[];
  signOut?: (e: any) => void;
  signOutLabel?: string;
}

const MobileUserMenu = ({
  accountMenuHeader,
  firstName,
  lastName,
  email,
  organizationName,
  clickMenuAction,
  accountSection,
  organizationSection,
  supportSection,
  signOut,
  signOutLabel,
}: Props) => {
  React.useEffect(() => {
    function handleKeyPress(e: any) {
      if (e.keyCode === 27) {
        clickMenuAction();
      }
    }

    document.addEventListener('keydown', handleKeyPress, false);
    return () => {
      document.removeEventListener('keydown', handleKeyPress, false);
    };
  }, [clickMenuAction]);

  return (
    <Menu onClick={() => {}} role="menu" aria-labelledby="UserMenuButton">
      <Top key="MobileUserMenuTopSection">
        <UIIconButton action={clickMenuAction} isInMobileMenu={true}>
          <ArrowLineLeft size="24px" color={COLORS.neutral_600} />
        </UIIconButton>
        <h3>{accountMenuHeader}</h3>
        <Right>
          <UIIconButton action={clickMenuAction} isInMobileMenu={true}>
            <Close size="24px" />
          </UIIconButton>
        </Right>
      </Top>
      {accountSection && accountSection?.length > 0 && (
        <MenuSection key="MobileAccountSection">
          <LabelLine>{`${firstName} ${lastName}`}</LabelLine>
          <MenuSectionList>
            {accountSection?.map(element => (
              <StyledMenulink key={element?.to} to={element?.to} menuText={element?.label} />
            ))}
          </MenuSectionList>
        </MenuSection>
      )}
      {organizationSection && organizationSection?.length > 0 && (
        <MenuSection key="MObileOrganizationSection">
          <LabelLine>{organizationName || ''}</LabelLine>
          <MenuSectionList>
            {organizationSection?.map(element => (
              <StyledMenulink key={element?.to} to={element?.to} menuText={element?.label} />
            ))}
          </MenuSectionList>
        </MenuSection>
      )}

      {supportSection && supportSection?.length > 0 && (
        <MenuSection key="MobileSupportSection">
          <LabelLine>{''}</LabelLine>
          <MenuSectionList>
            {supportSection?.map(element => (
              <StyledMenulink key={element?.to} isExternal={element.isExternal} to={element?.to} menuText={element?.label} />
            ))}
          </MenuSectionList>
        </MenuSection>
      )}
      {signOut && (
        <div key="SignoutButton" style={{ display: 'flex', margin: 'auto 0 12px 0' }}>
          <Button
            style={{ width: '90%', margin: '0 auto' }}
            variant="secondary"
            onClick={e => {
              e.preventDefault();
              signOut(e);
            }}>
            {signOutLabel}
          </Button>
        </div>
      )}
    </Menu>
  );
};

export default MobileUserMenu;
